import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { authAPI } from '../services/api';
import { useAuth } from '../context/AuthContext';

// Admin Profile — account details plus a password change form.
//
// Before this page existed the only way to change a password was to log out and
// use the emailed reset link, which needs working SMTP and access to the
// mailbox. The form posts through the same encrypted envelope as login, so
// neither the old nor the new password crosses the wire in clear text.
const C = {
  indigo: '#4f46e5',
  border: '#e2e8f0',
  text: '#0f172a',
  muted: '#64748b',
};

const styles = {
  // No max width: the two cards are meant to split the full content area
  // 50:50, and a cap left dead space to the right on wide screens.
  page: { padding: '24px' },
  heading: { fontSize: '22px', fontWeight: 700, color: C.text, margin: '0 0 4px' },
  sub: { fontSize: '14px', color: C.muted, margin: '0 0 24px' },
  card: {
    background: '#fff',
    border: `1px solid ${C.border}`,
    borderRadius: '12px',
    padding: '24px',
    boxShadow: '0 1px 3px rgba(15,23,42,.06)',
  },
  // Two equal halves. auto-fit collapses to a single column on narrow screens,
  // so no media query is needed. Cards stretch to a shared height so the row
  // reads as one 50:50 block rather than two ragged ones.
  grid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(330px, 1fr))',
    gap: '20px',
    alignItems: 'stretch',
  },
  cardTitle: { fontSize: '15px', fontWeight: 700, color: C.text, margin: '0 0 16px' },
  identity: { display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '20px' },
  avatar: {
    width: '56px',
    height: '56px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #4f46e5, #4338ca)',
    color: '#fff',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '22px',
    fontWeight: 600,
    flexShrink: 0,
  },
  rows: { borderTop: `1px solid ${C.border}` },
  row: {
    display: 'flex',
    justifyContent: 'space-between',
    gap: '16px',
    padding: '12px 0',
    borderBottom: `1px solid ${C.border}`,
    fontSize: '14px',
  },
  rowLabel: { color: C.muted },
  rowValue: { color: C.text, fontWeight: 500, wordBreak: 'break-all', textAlign: 'right' },
  field: { marginBottom: '18px' },
  label: { display: 'block', fontSize: '13px', fontWeight: 600, color: '#334155', marginBottom: '6px' },
  input: {
    width: '100%',
    padding: '10px 12px',
    fontSize: '14px',
    color: C.text,
    border: '1px solid #cbd5e1',
    borderRadius: '8px',
    outline: 'none',
    boxSizing: 'border-box',
  },
  hint: { fontSize: '12px', color: '#94a3b8', margin: '6px 0 0' },
  alert: (ok) => ({
    padding: '10px 12px',
    borderRadius: '8px',
    fontSize: '13px',
    marginBottom: '18px',
    background: ok ? '#ecfdf5' : '#fef2f2',
    color: ok ? '#065f46' : '#991b1b',
    border: `1px solid ${ok ? '#a7f3d0' : '#fecaca'}`,
  }),
  submit: (disabled) => ({
    padding: '10px 18px',
    fontSize: '14px',
    fontWeight: 600,
    color: '#fff',
    background: disabled ? '#a5b4fc' : C.indigo,
    border: 'none',
    borderRadius: '8px',
    cursor: disabled ? 'not-allowed' : 'pointer',
  }),
};

export default function AdminProfile() {
  const { admin, logout } = useAuth();
  const navigate = useNavigate();
  const signOutTimer = useRef(null);

  // The redirect fires on a timer so the confirmation is readable first; clear
  // it if the admin navigates away before it lands.
  useEffect(() => () => clearTimeout(signOutTimer.current), []);
  const [form, setForm] = useState({ currentPassword: '', newPassword: '', confirmPassword: '' });
  const [msg, setMsg] = useState(null);          // { ok: bool, text: string }
  const [saving, setSaving] = useState(false);
  const [signedOut, setSignedOut] = useState(false);   // success -> logging out

  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setMsg(null);

    // Confirmation is a client-only concern — the API never sees it.
    if (form.newPassword !== form.confirmPassword) {
      setMsg({ ok: false, text: 'New password and confirmation do not match' });
      return;
    }
    if (form.newPassword.length < 6) {
      setMsg({ ok: false, text: 'New password must be at least 6 characters' });
      return;
    }

    setSaving(true);
    try {
      const res = await authAPI.changePassword({
        currentPassword: form.currentPassword,
        newPassword: form.newPassword,
      });
      // The old credentials are gone, so the session should not outlive them.
      // Sign out and send the admin to the login screen to re-authenticate.
      setMsg({
        ok: true,
        text: `${res.data?.message || 'Password changed successfully'}. Signing you out — please log in with your new password.`,
      });
      setForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setSignedOut(true);
      signOutTimer.current = setTimeout(() => {
        logout();
        // `replace` so the back button cannot return to this page; and with the
        // token cleared every protected route bounces to /login anyway.
        navigate('/login', { replace: true });
      }, 1800);
      return;
    } catch (err) {
      // Prefer the server's reason. The generic fallbacks below only apply when
      // there is genuinely nothing to report — a network failure, or a status
      // with no body — so the message always says something actionable.
      const data = err.response?.data;
      const serverMsg = typeof data === 'string' ? data : (data?.error || data?.message);
      const status = err.response?.status;
      let text = serverMsg;
      if (!text) {
        if (!err.response) text = 'Could not reach the server. Check your connection and try again.';
        else if (status === 401) text = 'Your session has expired. Please sign in again.';
        else if (status === 500) text = 'Server error while changing the password. Please try again.';
        else text = `Failed to change password (HTTP ${status})`;
      }
      setMsg({ ok: false, text });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div style={styles.page}>
      <h1 style={styles.heading}>Admin Profile</h1>
      <p style={styles.sub}>Your account details and password.</p>

      <div style={styles.grid}>
      {/* Account details — moved here from the top bar */}
      <div style={styles.card}>
        <h2 style={styles.cardTitle}>Account Details</h2>
        <div style={styles.identity}>
          <div style={styles.avatar}>{admin?.name?.charAt(0)?.toUpperCase() || 'A'}</div>
          <div>
            <div style={{ fontSize: '17px', fontWeight: 600, color: C.text }}>{admin?.name || '-'}</div>
            <div style={{ fontSize: '13px', color: C.muted }}>{admin?.email || '-'}</div>
          </div>
        </div>
        <div style={styles.rows}>
          <div style={styles.row}>
            <span style={styles.rowLabel}>Name</span>
            <span style={styles.rowValue}>{admin?.name || '-'}</span>
          </div>
          <div style={styles.row}>
            <span style={styles.rowLabel}>Email</span>
            <span style={styles.rowValue}>{admin?.email || '-'}</span>
          </div>
          <div style={{ ...styles.row, borderBottom: 'none' }}>
            <span style={styles.rowLabel}>Admin ID</span>
            <span style={styles.rowValue}>{admin?.id ?? '-'}</span>
          </div>
        </div>
      </div>

      {/* Change password */}
      <div style={styles.card}>
        <h2 style={styles.cardTitle}>Change Password</h2>
        {msg && <div style={styles.alert(msg.ok)}>{msg.text}</div>}

        <form onSubmit={submit}>
          <div style={styles.field}>
            <label style={styles.label} htmlFor="currentPassword">Current password</label>
            <input
              id="currentPassword" name="currentPassword" type="password"
              autoComplete="current-password"
              value={form.currentPassword} onChange={onChange}
              style={styles.input} required disabled={signedOut}
            />
          </div>

          <div style={styles.field}>
            <label style={styles.label} htmlFor="newPassword">New password</label>
            <input
              id="newPassword" name="newPassword" type="password"
              autoComplete="new-password"
              value={form.newPassword} onChange={onChange}
              style={styles.input} required disabled={signedOut}
            />
            <p style={styles.hint}>At least 6 characters, and different from the current one.</p>
          </div>

          <div style={styles.field}>
            <label style={styles.label} htmlFor="confirmPassword">Confirm new password</label>
            <input
              id="confirmPassword" name="confirmPassword" type="password"
              autoComplete="new-password"
              value={form.confirmPassword} onChange={onChange}
              style={styles.input} required disabled={signedOut}
            />
          </div>

          <button type="submit" style={styles.submit(saving || signedOut)} disabled={saving || signedOut}>
            {signedOut ? 'Signing out...' : saving ? 'Saving...' : 'Change Password'}
          </button>
        </form>
      </div>
      </div>
    </div>
  );
}
